/* =========================================================
   Region & Language Modal

   Multiple togglers: any element anywhere on the page with
   data-rl-toggle opens this modal — not just elements inside this
   snippet's markup. Works via a single delegated click listener on
   document, same pattern as the nutrition sidebar:

     <button data-rl-toggle>Region & Language</button>

   Runs on window "load" rather than DOMContentLoaded, per request.

   EVERYTHING that binds a listener in this file is delegated on
   document, not bound to specific elements. This page also renders Vue
   components, which re-render/replace elements whenever their state
   changes — even elements with identical attributes come back as brand
   new nodes. A listener bound directly to one of those nodes
   (button.addEventListener(...)) is gone the instant Vue swaps it out.
   Delegating on document sidesteps this: the listener lives on a node
   Vue never touches, and every click/input/submit looks up the
   *current* matching element via closest(). Each delegated init
   function guards itself with a one-time flag on document.body so
   calling initAll() repeatedly (e.g. on shopify:section:load) never
   stacks up duplicate document-level listeners.

   The only thing that still runs per-modal-node is initModal(): that's
   not an event binding, just one-time state capture (original
   country/language, Weglot correction, flag rendering) for whichever
   modal node currently exists, guarded by modal.dataset.rlInit so it
   doesn't re-run for a node it's already set up.

   COUNTRY DROPDOWN: the "Deliver to" country list now lives inside a
   collapsible dropdown (data-rl-country-dropdown /
   data-rl-country-trigger / data-rl-country-panel) instead of always
   being visible, purely to save vertical space. None of the
   pin/popular/continent grouping, search-filter, or selection logic
   below changed — the dropdown just opens/closes on top of it, and
   the trigger button mirrors whichever country is currently selected.
   ========================================================= */

(function () {
  // Weglot now translates through Shopify's own native market URL, so
  // every save — for every country and language, no exceptions — goes
  // through the plain Shopify localization form. There's nothing
  // Weglot-specific left to special-case: no hardcoded language list,
  // no hardcoded currency map, no manual redirect. Country data
  // (available languages, currency, flag) all comes from each
  // country's own data-rl-languages / data-rl-currency attributes,
  // already rendered per-country by the liquid template — see
  // selectCountry below.

  // URL shape on this store: a single combined market segment, e.g.
  // /de-eu/products/x or /en-uk/products/x — Shopify builds this
  // itself for every country/language combination. This just needs
  // fully clearing before submitting a NEW locale change, so Shopify
  // builds the next one on a clean path instead of stacking on top of
  // a stale one. Loops so it's harmless if more than one prefix
  // segment is ever present. "/de-eu/products/x" -> "/products/x".
  // "/en-eu/" -> "/".
  function stripAllLocalePrefixes(pathname) {
    var result = pathname || '/';
    while (true) {
      var match = result.match(/^\/([a-z]{2}(-[a-z]{2})?)(?=\/|$|\?)/i);
      if (!match) break;
      var rest = result.slice(match[0].length);
      result = rest === '' ? '/' : rest;
    }
    return result;
  }

  // --- Spanish exception -------------------------------------------------
  // "es" is deliberately NOT a real Shopify-registered language on this
  // store (kept out of Settings > Languages so it doesn't conflict with
  // Weglot's own rendering/routing — see the region-language-modal.liquid
  // comments). Shopify's native localization form has no concept of it,
  // so it can never be POSTed as language_code like every other language
  // in this modal is. Instead, Weglot serves Spanish purely by URL shape:
  // combining "es" with whatever market suffix the store's OTHER
  // (Shopify-recognized) locale URLs already use, e.g. "es-eu" alongside
  // the real "de-eu"/"fr-eu"/"it-eu". These two helpers are the only
  // Spanish-specific logic in this file — everything else (selection,
  // active states, search, etc.) already works for "es" for free since
  // it's just another data-rl-iso value once the Liquid template renders
  // it as an option.
  function getMarketSuffixFromPath(pathname) {
    var match = (pathname || '').match(/^\/[a-z]{2}-([a-z]{2})(?=\/|$|\?)/i);
    return match ? match[1].toLowerCase() : null;
  }

  function buildSpanishUrl(marketSuffix, pathname, search) {
    var cleanPath = stripAllLocalePrefixes(pathname || '/');
    var prefix = marketSuffix ? '/es-' + marketSuffix : '/es';
    var joinedPath = cleanPath === '/' ? prefix : prefix + cleanPath;
    return window.location.origin + joinedPath + (search || '');
  }

  /* ---------------------------------------------------------------------
   * Emoji flag fallback
   * Most flags now render as real <img> tags directly from Liquid, via
   * snippets/flag-icon.liquid (covers the 12 countries + 2 continents
   * with uploaded artwork). This function only ever runs against the
   * countries/continents NOT in that set — flag-icon.liquid falls back
   * to emitting a <span data-rl-flag data-iso="..."> for those, which is
   * what this code looks for. Real <img> flags have no data-rl-flag
   * attribute at all, so this simply has nothing to do for them.
   * ------------------------------------------------------------------- */

  function flagEmoji(isoCode) {
    if (!isoCode || isoCode.length !== 2) return '';
    var code = isoCode.toUpperCase();
    var first = 127397 + code.charCodeAt(0);
    var second = 127397 + code.charCodeAt(1);
    return String.fromCodePoint(first) + String.fromCodePoint(second);
  }

  function renderFlags(root) {
    root.querySelectorAll('[data-rl-flag]').forEach(function (el) {
      var iso = el.getAttribute('data-iso');
      el.textContent = flagEmoji(iso);
    });
  }

  /* ---------------------------------------------------------------------
   * Country dropdown open/close — purely presentational, sits on top of
   * the existing country list/search/select logic without changing it.
   * ------------------------------------------------------------------- */

  function isCountryDropdownOpen(dropdown) {
    if (!dropdown) return false;
    var panel = dropdown.querySelector('[data-rl-country-panel]');
    return !!panel && !panel.hidden;
  }

  function openCountryDropdown(dropdown) {
    if (!dropdown) return;
    var panel = dropdown.querySelector('[data-rl-country-panel]');
    var trigger = dropdown.querySelector('[data-rl-country-trigger]');
    if (panel) panel.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    dropdown.classList.add('is-open');
  }

  function closeCountryDropdown(dropdown) {
    if (!dropdown) return;
    var panel = dropdown.querySelector('[data-rl-country-panel]');
    var trigger = dropdown.querySelector('[data-rl-country-trigger]');
    if (panel) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    dropdown.classList.remove('is-open');
  }

  function toggleCountryDropdown(dropdown) {
    if (isCountryDropdownOpen(dropdown)) {
      closeCountryDropdown(dropdown);
    } else {
      openCountryDropdown(dropdown);
    }
  }

  function openModal(toggler) {
    var modal = document.querySelector('[data-rl-modal]');
    if (!modal) return;

    modal.hidden = false;
    document.documentElement.classList.add('rl-modal-open');
  }

  function resetSelection(modal) {
    var originalButton = modal.__rlOriginalButton;
    if (originalButton) {
      // selectCountry always resets the language to the (re-selected)
      // country's default language — restore the language that was
      // actually active when the modal was opened afterward, so
      // cancel/close doesn't silently change the user's language.
      selectCountry(modal, originalButton);
      selectLanguageByIso(modal, modal.dataset.rlOriginalLanguage);
      updateSaveButtonState(modal);
      return;
    }

    // Fallback if we somehow never found the original country's button
    // (e.g. it was popular-pinned and something went wrong locating it):
    // at least restore the hidden field and disable Save directly.
    var countryInput = modal.closest("form").querySelector('[data-rl-country-input]');
    if (countryInput) countryInput.value = modal.dataset.rlOriginalCountry;
    updateSaveButtonState(modal);
  }

  function closeModal() {
    var modal = document.querySelector('[data-rl-modal]');
    if (!modal) return;

    modal.hidden = true;
    document.documentElement.classList.remove('rl-modal-open');
    resetSelection(modal);

    // Belt-and-braces: resetSelection() already collapses the dropdown
    // via selectCountry() whenever it finds an original button to
    // restore, but make sure it's closed either way (e.g. the fallback
    // branch above, or the dropdown was left open with no selection
    // made at all).
    modal.querySelectorAll('[data-rl-country-dropdown]').forEach(function (dropdown) {
      closeCountryDropdown(dropdown);
    });
  }

  /* ---------------------------------------------------------------------
   * Save button state — the button is always enabled/clickable, even
   * with no changes made. Whether a click actually submits the form is
   * decided separately in the delegated submit handler below (submitting
   * is a no-op if neither country nor language changed from what was
   * active when the modal was opened). This function just makes sure
   * nothing upstream (e.g. a failed fetch re-enabling it, or
   * resetSelection) leaves it looking disabled.
   * ------------------------------------------------------------------- */

  function updateSaveButtonState(modal) {
    var saveButton = modal.querySelector('[data-rl-save]');
    if (!saveButton) return;

    saveButton.disabled = false;
    saveButton.classList.remove('is-disabled');
  }

  // Selects a specific language button/value within the currently
  // rendered language list, if that iso exists among the options.
  // Shared by selectCountry's default pick, manual clicks, and
  // resetSelection's restore-on-cancel. Returns true if the iso was
  // found and applied.
  //
  // NOTE: the language_code hidden input lives in the surrounding
  // <form>, as a SIBLING of [data-rl-modal] — not nested inside it —
  // so it must be looked up via modal.closest("form"), same as the
  // country input. A plain modal.querySelector() here always misses.
  function selectLanguageByIso(modal, iso) {
    var languageList = modal.querySelector('[data-rl-language-list]');
    var languageInput = modal.closest("form").querySelector('[data-rl-language-input]');
    if (!iso || !languageList) return false;

    var button = languageList.querySelector('[data-rl-language-option][data-rl-iso="' + iso + '"]');
    if (!button) return false;

    if (languageInput) languageInput.value = iso;
    languageList.querySelectorAll('[data-rl-language-option]').forEach(function (el) {
      el.classList.toggle('rl-modal__option--active', el === button);
    });
    return true;
  }

  // Used only to make the modal SHOW the correct language as selected
  // when it opens (Shopify's own hidden input can lag behind what's
  // actually active on the page). Purely cosmetic — every save still
  // goes through Shopify's own form untouched. Wrapped defensively in
  // case Weglot hasn't loaded.
  function getWeglotCurrentLang() {
    if (window.Weglot && typeof window.Weglot.getCurrentLang === 'function') {
      try {
        return window.Weglot.getCurrentLang() || null;
      } catch (err) {
        return null;
      }
    }
    return null;
  }

  function selectCountry(modal, button) {
    var iso = button.getAttribute('data-rl-iso');
    var name = button.getAttribute('data-rl-name');
    var languages = [];
    var currency = {};

    try {
      languages = JSON.parse(button.getAttribute('data-rl-languages') || '[]');
    } catch (err) {
      languages = [];
    }
    try {
      currency = JSON.parse(button.getAttribute('data-rl-currency') || '{}');
    } catch (err) {
      currency = {};
    }

    // Update hidden form field
    var countryInput = modal.closest("form").querySelector('[data-rl-country-input]');
    if (countryInput) countryInput.value = iso;

    // Update active state on all country rows
    modal.querySelectorAll('[data-rl-country-option]').forEach(function (el) {
      el.classList.toggle('rl-modal__country--active', el === button);
    });

    // Update "Available for X" subtitles
    modal.querySelectorAll('[data-rl-language-subtitle], [data-rl-currency-subtitle]').forEach(function (el) {
      el.textContent = name;
    });

    // Re-render the language section for the newly selected country,
    // showing EVERY language Shopify lists as available for it (mirrors
    // the server-side loop in the liquid template), defaulting to
    // whichever one Shopify itself lists FIRST — no hardcoding, just
    // whatever data-rl-languages says for this country. The user can
    // still click any other listed language afterward via the delegated
    // language-selection handler.
    // languageInput lives outside the modal (sibling in the <form>),
    // same as countryInput above.
    var languageList = modal.querySelector('[data-rl-language-list]');
    var languageInput = modal.closest("form").querySelector('[data-rl-language-input]');

    var defaultLanguage = languages.length > 0 ? languages[0] : null;

    if (languageInput) languageInput.value = defaultLanguage ? defaultLanguage.iso_code : '';

    if (languageList) {
      languageList.innerHTML = languages.map(function (lang) {
        var isActive = defaultLanguage && lang.iso_code === defaultLanguage.iso_code;
        return (
          '<button type="button" class="rl-modal__option' + (isActive ? ' rl-modal__option--active' : '') + '" data-rl-language-option data-rl-iso="' + lang.iso_code + '">' +
            '<span>' + lang.endonym_name + '</span>' +
            '<svg class="rl-modal__option-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
          '</button>'
        );
      }).join('');
    }

    // Update the currency display
    var symbolEl = modal.querySelector('[data-rl-currency-symbol]');
    var codeEl = modal.querySelector('[data-rl-currency-code]');
    var nameEl = modal.querySelector('[data-rl-currency-name]');
    if (symbolEl) symbolEl.textContent = currency.symbol || '';
    if (codeEl) codeEl.textContent = currency.iso_code || '';
    if (nameEl) nameEl.textContent = currency.name || '';

    // Sync the collapsed dropdown trigger to show the newly selected
    // country (flag + name), and collapse the dropdown back down —
    // mirrors a native <select>. The flag element is cloned straight off
    // the clicked country button so it works whether that flag is a real
    // <img> or the emoji-fallback <span> (already resolved by
    // renderFlags() well before any click can happen).
    var dropdown = modal.querySelector('[data-rl-country-dropdown]');
    if (dropdown) {
      var trigger = dropdown.querySelector('[data-rl-country-trigger]');
      if (trigger) {
        var triggerNameEl = trigger.querySelector('[data-rl-country-trigger-name]');
        if (triggerNameEl) triggerNameEl.textContent = name;

        var triggerFlagEl = trigger.querySelector('.rl-modal__flag');
        var sourceFlagEl = button.querySelector('.rl-modal__flag');
        if (triggerFlagEl && sourceFlagEl) {
          triggerFlagEl.replaceWith(sourceFlagEl.cloneNode(true));
        }
      }
      closeCountryDropdown(dropdown);
    }

    updateSaveButtonState(modal);
  }

  /* ---------------------------------------------------------------------
   * DELEGATED EVENT HANDLERS — all bound on document, all guarded with a
   * one-time flag on document.body, all resolving the actual element
   * they need via closest() at the moment of the event rather than at
   * init time. See the file-level comment at the top for why.
   * ------------------------------------------------------------------- */

  function initTogglers() {
    if (document.body.dataset.rlTogglersDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var toggler = event.target.closest('[data-rl-toggle]');
      if (!toggler) return;

      event.preventDefault();
      openModal(toggler);
    });

    document.body.dataset.rlTogglersDelegated = 'true';
  }

  function initCloseTriggersDelegation() {
    if (document.body.dataset.rlCloseDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-rl-close], [data-rl-cancel], [data-rl-overlay]');
      if (!trigger) return;
      closeModal();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var modal = document.querySelector('[data-rl-modal]');
      if (!modal || modal.hidden) return;

      // If the country dropdown is open, Escape closes just that first
      // — same "innermost first" behavior people expect from a native
      // <select> — rather than closing the whole modal underneath it.
      var openDropdown = modal.querySelector('[data-rl-country-dropdown].is-open');
      if (openDropdown) {
        closeCountryDropdown(openDropdown);
        return;
      }

      closeModal();
    });

    document.body.dataset.rlCloseDelegated = 'true';
  }

  /* Country dropdown toggling — clicking the trigger opens/closes its
   * panel; clicking ANYWHERE else closes it, with exactly one
   * exception: the search input, since focusing/typing there is
   * itself what's supposed to open the dropdown (see
   * initSearchDelegation below) — closing it out from under an active
   * search would fight that behavior on every keystroke. Everything
   * else — the "Deliver to:" label, blank space in the column, the
   * language/currency side, the header, the footer, the overlay — all
   * count as "outside" and close it, not just clicks strictly outside
   * the whole country column. */
  function initCountryDropdownDelegation() {
    if (document.body.dataset.rlCountryDropdownDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-rl-country-trigger]');
      if (trigger) {
        var dropdown = trigger.closest('[data-rl-country-dropdown]');
        toggleCountryDropdown(dropdown);
        return;
      }

      // Inside the dropdown itself (trigger + panel + every country/
      // continent row in it) is never "outside".
      if (event.target.closest('[data-rl-country-dropdown]')) return;

      // The search input is the one exception outside the dropdown
      // proper — see the function comment above.
      if (event.target.closest('[data-rl-search-input]')) return;

      document.querySelectorAll('[data-rl-country-dropdown].is-open').forEach(function (dropdown) {
        closeCountryDropdown(dropdown);
      });
    });

    document.body.dataset.rlCountryDropdownDelegated = 'true';
  }

  /* Country selection — updates the hidden country_code input and
   * re-renders the Language/Currency panels from the country's own
   * data-rl-languages / data-rl-currency JSON (embedded at render
   * time), with no page reload. */
  function initCountrySelectionDelegation() {
    if (document.body.dataset.rlCountryDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-rl-country-option]');
      if (!button) return;

      var modal = button.closest('[data-rl-modal]');
      if (!modal) return;

      selectCountry(modal, button);
    });

    document.body.dataset.rlCountryDelegated = 'true';
  }

  /* Language selection — updates the hidden language_code input and
   * active state on click. Delegated on document (not just the
   * language-list container) since selectCountry replaces that
   * container's innerHTML wholesale on every country change. */
  function initLanguageSelectionDelegation() {
    if (document.body.dataset.rlLanguageDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-rl-language-option]');
      if (!button) return;

      var modal = button.closest('[data-rl-modal]');
      if (!modal) return;

      selectLanguageByIso(modal, button.getAttribute('data-rl-iso'));
    });

    document.body.dataset.rlLanguageDelegated = 'true';
  }

  /* Country search filter. 'input' bubbles, so this delegates cleanly
   * the same way the click handlers do. The search input now lives
   * outside the dropdown panel (it stays visible even while the
   * dropdown is collapsed), so it also opens the dropdown on focus/
   * input and scrolls the list to the first match — the list-filtering
   * logic itself is untouched. */
  function initSearchDelegation() {
    if (document.body.dataset.rlSearchDelegated === 'true') return;

    function dropdownForSearchInput(input) {
      var shipColumn = input.closest('.rl-modal__col--ship');
      return shipColumn ? shipColumn.querySelector('[data-rl-country-dropdown]') : null;
    }

    // 'focus' doesn't bubble, so this listener needs the capture phase
    // to catch it via delegation on document.
    // document.addEventListener('focus', function (event) {
    //   var input = event.target && event.target.closest && event.target.closest('[data-rl-search-input]');
    //   if (!input) return;
    //   openCountryDropdown(dropdownForSearchInput(input));
    // }, true);

    document.addEventListener('input', function (event) {
      var input = event.target.closest('[data-rl-search-input]');
      if (!input) return;

      var modal = input.closest('[data-rl-modal]');
      if (!modal) return;

      openCountryDropdown(dropdownForSearchInput(input));

      var query = input.value.trim().toLowerCase();

      modal.querySelectorAll('[data-rl-country-option]').forEach(function (button) {
        var name = (button.getAttribute('data-rl-name') || '').toLowerCase();
        var matches = !query || name.indexOf(query) !== -1;
        if (matches) {
          button.removeAttribute('data-rl-filtered-out');
        } else {
          button.setAttribute('data-rl-filtered-out', 'true');
        }
      });

      // Hide a continent group entirely if every country inside it is
      // filtered out, and auto-expand groups that have a match so
      // results aren't hidden behind a collapsed accordion.
      modal.querySelectorAll('[data-rl-continent-group]').forEach(function (group) {
        var countries = group.querySelectorAll('[data-rl-country-option]');
        var anyVisible = Array.prototype.some.call(countries, function (c) {
          return !c.hasAttribute('data-rl-filtered-out');
        });

        if (anyVisible) {
          group.removeAttribute('data-rl-filtered-out');
          if (query) group.classList.add('is-open');
        } else {
          group.setAttribute('data-rl-filtered-out', 'true');
        }
      });

      // Auto-navigate to the search term: scroll the (now open) list to
      // the first visible match so the customer doesn't have to hunt
      // for it themselves.
      if (query) {
        var firstMatch = modal.querySelector('[data-rl-country-option]:not([data-rl-filtered-out])');
        if (firstMatch && firstMatch.scrollIntoView) {
          firstMatch.scrollIntoView({ block: 'nearest' });
        }
      }
    });

    document.body.dataset.rlSearchDelegated = 'true';
  }

  /* ---------------------------------------------------------------------
   * Form submission on Save. Every save — every country, every
   * language — goes through the plain Shopify localization form (POST
   * via fetch, follow the redirect to response.url). Weglot now
   * translates through Shopify's own native market URL, so there's no
   * separate manual redirect path anymore; Shopify's own combined
   * segment (e.g. /de-eu/products/x) is exactly what's needed either
   * way.
   *
   * The return_to field, the form's action path, and the request's
   * Referer (see note below) are all fully cleared first so Shopify
   * builds its new market segment on a clean base rather than stacking
   * on top of a leftover one.
   *
   * If BOTH country and language changed together (e.g. switching to
   * Switzerland AND picking French), this is split into TWO sequential
   * requests: step 1 submits the country change alone (omitting
   * language_code), step 2 submits the language change alone (omitting
   * country_code) against the market step 1 just established.
   *
   * The two requests need DIFFERENT Referer handling, which is the
   * actual reason a combined save was landing on the shop's overall
   * default language (e.g. "en-GB") instead of the one picked:
   * Shopify's redirect logic reads the CURRENT market from the
   * Referer header's path, not from return_to or the action URL.
   *   - Step 1 (leaving the OLD country's context) strips the
   *     Referer's locale prefix, same as a normal single-field save —
   *     see the note on refererUrl below.
   *   - Step 2 (applying language WITHIN the country step 1 just
   *     established) must NOT strip it — the prefix step 1's response
   *     redirected to (e.g. "/en-ch") IS the market context the
   *     language change needs to apply inside. Stripping it here told
   *     Shopify "no market established", so it fell back to computing
   *     everything from scratch.
   *
   * Delegated on document via the 'submit' event (which bubbles), same
   * as everything else above. Because a delegated submit listener would
   * otherwise fire for every form on the page (add-to-cart, newsletter,
   * etc.), the handler bails out immediately — before touching
   * preventDefault — for any submitted form that doesn't contain
   * [data-rl-modal].
   * ------------------------------------------------------------------- */

  function initFormSubmitDelegation() {
    if (document.body.dataset.rlFormSubmitDelegated === 'true') return;

    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form');
      if (!form) return;

      var modal = form.querySelector('[data-rl-modal]');
      if (!modal) return;

      event.preventDefault();
      event.stopPropagation();

      // Shared by every request this handler makes: builds the stripped
      // action URL and scrubs the return_to field of any leftover prefix
      // segments (e.g. still "/de-eu/products/x" from before), so
      // Shopify's own redirect logic builds its new market segment on a
      // clean path instead of stacking on top of a stale one.
      function buildActionUrl() {
        var returnToInput = form.querySelector('input[name="return_to"]');
        if (returnToInput && returnToInput.value) {
          returnToInput.value = stripAllLocalePrefixes(returnToInput.value);
        }

        try {
          var actionUrlObj = new URL(form.action, window.location.origin);
          actionUrlObj.pathname = stripAllLocalePrefixes(actionUrlObj.pathname);
          return actionUrlObj.href;
        } catch (err) {
          console.error('Region & Language: failed to build action URL, falling back', err);
          return form.action || window.location.href;
        }
      }

      // Posts the localization form to Shopify. `fieldOverrides` lets a
      // caller force specific fields onto the FormData snapshot taken
      // from the form's current DOM state — pass a key with value `null`
      // to OMIT that field entirely (used to leave language_code out of
      // the country-only leg of a two-step save below); omit a key
      // altogether to just use the form's current value as-is.
      // `refererPath` is the full origin+path Shopify should treat as
      // "where the user currently is" (see the Referer note above).
      function postLocalization(fieldOverrides, refererPath) {
        var actionUrl = buildActionUrl();
        var formData = new FormData(form);

        Object.keys(fieldOverrides || {}).forEach(function (key) {
          if (fieldOverrides[key] === null) {
            formData.delete(key);
          } else {
            formData.set(key, fieldOverrides[key]);
          }
        });

        // fetch() automatically sends a Referer header set to the
        // ACTUAL current page URL (still carrying whatever market
        // segment is currently active) — and Shopify's redirect appears
        // to build its new market segment on top of THAT, not the
        // return_to field or the action URL, which is why a stale prefix
        // still leaked through even with both of those stripped.
        // Override it explicitly to the already-stripped path.
        return fetch(actionUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          referrer: refererPath
        });
      }

      var countryInput = form.querySelector('[data-rl-country-input]');
      var languageInput = form.querySelector('[data-rl-language-input]');
      var countryIso = countryInput ? countryInput.value : '';
      var languageIso = languageInput ? languageInput.value : '';

      var countryChanged = countryIso !== modal.dataset.rlOriginalCountry;
      var languageChanged = languageIso !== modal.dataset.rlOriginalLanguage;

      // Save Preferences is always clickable (see updateSaveButtonState),
      // but there's nothing to persist if neither selection actually
      // moved from what was active when the modal opened — just close
      // instead of hitting the network / redirecting.
      if (!countryChanged && !languageChanged) {
        closeModal();
        return;
      }

      var saveButton = modal.querySelector('[data-rl-save]');
      if (saveButton) saveButton.disabled = true;

      var refererUrl = window.location.origin
        + stripAllLocalePrefixes(window.location.pathname)
        + window.location.search;

      function handleSaveFailure(err) {
        console.error('Region & Language: failed to save preferences', err);
        if (saveButton) saveButton.disabled = false;
      }

      // Spanish exception — see the getMarketSuffixFromPath/buildSpanishUrl
      // comment above. Every other language falls through to the normal
      // Shopify-native branches below unchanged.
      if (languageIso === 'es') {
        if (countryChanged) {
          // Let Shopify resolve the COUNTRY change (and whatever default
          // language it lands on for that market) first — same as the
          // country-only branch below — then override the URL's language
          // segment to "es" ourselves instead of taking Shopify's default.
          postLocalization({ language_code: null }, refererUrl)
            .then(function (countryResponse) {
              var afterCountryUrl = countryResponse.url || refererUrl;
              var afterCountryPath;
              try {
                afterCountryPath = new URL(afterCountryUrl, window.location.origin).pathname;
              } catch (err) {
                afterCountryPath = window.location.pathname;
              }
              var marketSuffix = getMarketSuffixFromPath(afterCountryPath);
              window.location.href = buildSpanishUrl(marketSuffix, afterCountryPath, window.location.search);
            })
            .catch(handleSaveFailure);
          return;
        }

        // Language-only change to Spanish — country unchanged, so the
        // current URL already carries the right market suffix. No
        // network request needed at all; Weglot's own subfolder routing
        // handles the rest once we navigate.
        var currentMarketSuffix = getMarketSuffixFromPath(window.location.pathname);
        window.location.href = buildSpanishUrl(currentMarketSuffix, window.location.pathname, window.location.search);
        return;
      }

      if (countryChanged && languageChanged) {
        // Two-step save — see the comment block above this function.
        postLocalization({ language_code: null }, refererUrl)
          .then(function (countryResponse) {
            var afterCountryUrl = countryResponse.url || refererUrl;
            var afterCountryPath;
            try {
              afterCountryPath = new URL(afterCountryUrl, window.location.origin).pathname;
            } catch (err) {
              afterCountryPath = window.location.pathname;
            }
            // Deliberately NOT stripped here, unlike refererUrl above —
            // this IS the market prefix (e.g. "/en-ch") Shopify just
            // established in step 1, and Shopify's redirect logic reads
            // the current market from the Referer path. Stripping it
            // would tell Shopify "no market established", making it
            // fall back to computing everything from scratch (which is
            // what caused the language change to land on the shop's
            // overall default locale instead of applying within CH).
            var afterCountryReferer = window.location.origin
              + afterCountryPath
              + window.location.search;
            return postLocalization({ country_code: null }, afterCountryReferer);
          })
          .then(function (finalResponse) {
            window.location.href = finalResponse.url || window.location.href;
          })
          .catch(handleSaveFailure);
        return;
      }

      if (countryChanged) {
        // Country-only change — omit language_code so Shopify picks
        // its own default for the new market (same result the UI
        // already shows, since selectCountry mirrors that same
        // default client-side).
        postLocalization({ language_code: null }, refererUrl)
          .then(function (response) {
            window.location.href = response.url || window.location.href;
          })
          .catch(handleSaveFailure);
        return;
      }

      // Language-only change (country unchanged) — omit country_code
      // entirely for the same reason as above: its mere presence in
      // the body, even unchanged, appears to make Shopify recompute
      // the market's default language and drop the language_code sent
      // alongside it.
      postLocalization({ country_code: null }, refererUrl)
        .then(function (response) {
          window.location.href = response.url || window.location.href;
        })
        .catch(handleSaveFailure);
    });

    document.body.dataset.rlFormSubmitDelegated = 'true';
  }

  /* ---------------------------------------------------------------------
   * One-time per-modal-node setup. Not an event binding — just capturing
   * the state a fresh modal node starts with. Guarded by
   * modal.dataset.rlInit in initAll() below so it only runs once per
   * node; if Vue ever swaps in a wholly new modal node, that new node
   * has no rlInit flag yet and gets set up correctly on the next
   * initAll() pass.
   * ------------------------------------------------------------------- */

  function initModal(modal) {
    var countryInput = modal.closest("form").querySelector('[data-rl-country-input]');
    var languageInput = modal.closest("form").querySelector('[data-rl-language-input]');

    // Shopify's own server-rendered localization.country / .language are
    // always correct now that every save goes through its own native
    // form — no client-side override needed (unlike the old manual
    // Weglot redirect, which bypassed Shopify and needed a cookie-based
    // correction here).
    modal.dataset.rlOriginalCountry = countryInput ? countryInput.value : '';
    modal.dataset.rlOriginalLanguage = languageInput ? languageInput.value : '';
    modal.__rlOriginalButton = modal.querySelector(
      '[data-rl-country-option][data-rl-iso="' + modal.dataset.rlOriginalCountry + '"]'
    );

    // Display-only correction: if Weglot reports a different current
    // language than Shopify's hidden input, trust Weglot for what the
    // modal SHOWS as selected. Update the hidden input and dataset too
    // so the visible state and "did anything change" check stay in
    // sync — but this never changes how a save is built; that logic
    // above is untouched and still driven entirely by the form fields.
    var weglotCurrentLang = getWeglotCurrentLang();
    if (weglotCurrentLang && weglotCurrentLang !== modal.dataset.rlOriginalLanguage) {
      modal.dataset.rlOriginalLanguage = weglotCurrentLang;
      selectLanguageByIso(modal, weglotCurrentLang);
    }

    renderFlags(modal);
    updateSaveButtonState(modal);

    // Dropdown always starts closed for a freshly-initialized modal node.
    modal.querySelectorAll('[data-rl-country-dropdown]').forEach(function (dropdown) {
      closeCountryDropdown(dropdown);
    });

    // Continent groups are no longer collapsible — there's no click
    // handler for data-rl-continent-toggle any more (see the removed
    // initContinentToggleDelegation above), so every continent's
    // countries need to be visible from the start rather than relying
    // on a toggle the user can never trigger. is-open is what the CSS
    // (.rl-modal__continent.is-open > .rl-modal__continent-panel)
    // expands, so this simply forces it on for every group, regardless
    // of whichever single continent Liquid may have marked expanded
    // server-side (current_country_continent_key).
    modal.querySelectorAll('[data-rl-continent-group]').forEach(function (group) {
      group.classList.add('is-open');
      var toggle = group.querySelector('[data-rl-continent-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    });
  }

  function initAll() {
    initTogglers();
    initCloseTriggersDelegation();
    initCountryDropdownDelegation();
    initCountrySelectionDelegation();
    initLanguageSelectionDelegation();
    initSearchDelegation();
    initFormSubmitDelegation();

    // Emoji-flag fallback runs document-wide, not just inside the modal —
    // flag-icon.liquid can be used elsewhere on the page too, e.g. the
    // announcement bar's country flag.
    renderFlags(document);

    document.querySelectorAll('[data-rl-modal]').forEach(function (modal) {
      if (modal.dataset.rlInit === 'true') return;
      initModal(modal);
      modal.dataset.rlInit = 'true';
    });
  }

  // if (document.readyState === 'loading') {
  //   document.addEventListener('DOMContentLoaded', initAll);
  // } else {
  //   initAll();
  // }

  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll);
  }

  document.addEventListener('shopify:section:load', initAll);
})();